/**
 * OpsFlow Push Notification Service
 * 
 * Handles Web Push API integration for real-time notifications.
 * Works with PWA on both desktop and mobile browsers.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Race a promise against a timeout. Rejects with message if the timeout wins. */
function withTimeout(promise, ms, message) {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(message)), ms);
    })
  ]).finally(() => clearTimeout(timer));
}

/**
 * Convert URL-safe base64 string to Uint8Array
 * Required for the applicationServerKey in pushManager.subscribe()
 */
function urlBase64ToUint8Array(base64String) {
  // Add padding if needed
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Convert ArrayBuffer to URL-safe base64 string
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Check if Web Push is supported in this browser
 */
export function isPushSupported() {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Get current notification permission status
 * @returns 'granted' | 'denied' | 'default' | 'unsupported'
 */
export function getNotificationPermission() {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

/**
 * Request notification permission from the user
 * @returns Promise<'granted' | 'denied' | 'default'>
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    throw new Error('Notifications not supported in this browser');
  }
  
  const permission = await Notification.requestPermission();
  console.log('[Push] Permission result:', permission);
  return permission;
}

/**
 * Register the service worker at app startup.
 * Call this once from the app root so the SW is always available
 * for subscription checks on any page.
 */
export async function registerServiceWorker() {
  if (!isPushSupported()) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    console.log('[Push] Service worker registered at app root');
    return registration;
  } catch (err) {
    console.error('[Push] Service worker registration failed:', err);
    return null;
  }
}

/**
 * Get the current push subscription if one exists.
 * Uses getRegistration() (returns immediately) instead of .ready (hangs if no SW).
 * @returns Promise<PushSubscription | null>
 */
export async function getPushSubscription() {
  if (!isPushSupported()) {
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.getRegistration('/');
    if (!registration) {
      return null;
    }
    const subscription = await registration.pushManager.getSubscription();
    return subscription;
  } catch (err) {
    console.error('[Push] Error getting subscription:', err);
    return null;
  }
}

/**
 * Subscribe to push notifications (idempotent).
 *
 * 1. Ensures SW is registered and active (with timeout).
 * 2. Reuses an existing pushManager subscription if present.
 * 3. Saves to the backend via an idempotent upsert (with AbortController timeout).
 *
 * Every await that can hang is wrapped in withTimeout so the caller's
 * finally{} block always executes.
 *
 * @param token - JWT auth token
 * @returns Promise<PushSubscription>
 */
export async function subscribeToPush(token) {
  if (!isPushSupported()) {
    throw new Error('Push notifications not supported in this browser');
  }
  if (!token) {
    throw new Error('Authentication required');
  }

  console.log('[Push] Starting subscription process...');

  // ── Step 1: Ensure we have an active SW registration (timeout 10s) ──
  let registration = await navigator.serviceWorker.getRegistration('/');
  if (!registration) {
    registration = await withTimeout(
      navigator.serviceWorker.register('/sw.js', { scope: '/' }),
      10000,
      'Service worker registration timed out'
    );
  }

  // If the SW is still installing / waiting, give it a moment to activate
  if (!registration.active) {
    await withTimeout(
      navigator.serviceWorker.ready,
      10000,
      'Service worker activation timed out'
    );
    // Re-read in case ready returned a different reg object
    registration = await navigator.serviceWorker.getRegistration('/');
  }
  console.log('[Push] Service worker active');

  // ── Step 2: Get VAPID public key from server (timeout 10s) ──
  const ctrl1 = new AbortController();
  const keyTimer = setTimeout(() => ctrl1.abort(), 10000);
  let publicKey;
  try {
    const resp = await fetch(`${API_URL}/api/push/vapid-key`, { signal: ctrl1.signal });
    if (!resp.ok) throw new Error('Server error');
    const data = await resp.json();
    publicKey = data.publicKey;
    if (!publicKey) throw new Error('Push not configured on server');
  } catch (err) {
    if (err.name === 'AbortError') throw new Error('VAPID key request timed out');
    throw new Error('Could not connect to notification server');
  } finally {
    clearTimeout(keyTimer);
  }

  // ── Step 3: Reuse existing subscription or create a new one ──
  let subscription = await registration.pushManager.getSubscription();

  // If existing subscription's applicationServerKey doesn't match the
  // current VAPID key we must recreate it.
  if (subscription) {
    try {
      const existingKeyBuf = subscription.options?.applicationServerKey;
      const currentKey = urlBase64ToUint8Array(publicKey);
      const existingKey = existingKeyBuf ? new Uint8Array(existingKeyBuf) : null;
      const keysMatch = existingKey &&
        existingKey.length === currentKey.length &&
        existingKey.every((b, i) => b === currentKey[i]);

      if (keysMatch) {
        console.log('[Push] Reusing existing browser subscription');
      } else {
        console.log('[Push] VAPID key changed — recreating subscription');
        await subscription.unsubscribe();
        subscription = null;
      }
    } catch (_) {
      // If comparison fails, recreate to be safe
      try { await subscription.unsubscribe(); } catch (__) {}
      subscription = null;
    }
  }

  if (!subscription) {
    console.log('[Push] Creating new push subscription...');
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        throw new Error('Notification permission was denied. Please enable in browser settings.');
      }
      throw new Error('Could not create push subscription: ' + err.message);
    }
  }

  // ── Step 4: Save to backend — idempotent upsert (timeout 12s) ──
  const p256dh = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');
  if (!p256dh || !auth) throw new Error('Invalid subscription keys');

  const ctrl2 = new AbortController();
  const saveTimer = setTimeout(() => ctrl2.abort(), 12000);
  try {
    const resp = await fetch(`${API_URL}/api/push/subscribe`, {
      method: 'POST',
      signal: ctrl2.signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(p256dh),
          auth: arrayBufferToBase64(auth)
        },
        device_type: /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
      })
    });

    const result = await resp.json();
    if (!resp.ok || result.success === false) {
      throw new Error(result.detail || result.message || 'Server rejected subscription');
    }
    console.log('[Push] Subscription saved');
    return subscription;
  } catch (err) {
    if (err.name === 'AbortError') throw new Error('Backend save timed out — please try again');
    throw new Error('Could not save subscription: ' + err.message);
  } finally {
    clearTimeout(saveTimer);
  }
}

/**
 * Unsubscribe from push notifications
 * @param token - JWT auth token for API calls
 */
export async function unsubscribeFromPush(token) {
  if (!isPushSupported()) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration('/');
    if (!registration) return;
    
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      console.log('[Push] Unsubscribed locally');
      
      if (token) {
        await fetch(`${API_URL}/api/push/unsubscribe`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ endpoint: subscription.endpoint })
        });
        console.log('[Push] Subscription removed from server');
      }
    }
  } catch (err) {
    console.error('[Push] Error unsubscribing:', err);
  }
}

/**
 * Send a test notification to verify everything is working
 * @param token - JWT auth token for API calls
 */
export async function sendTestNotification(token) {
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_URL}/api/push/test`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  return response.json();
}

/**
 * Listen for subscription changes (e.g., when subscription expires)
 * Call this on app startup to handle re-subscription
 */
export function setupPushSubscriptionListener(onSubscriptionChange) {
  if (!isPushSupported()) {
    return;
  }

  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGED') {
      console.log('[Push] Subscription changed, notifying app...');
      if (onSubscriptionChange) {
        onSubscriptionChange(event.data.subscription);
      }
    }
  });
}

/**
 * Validate the current subscription and auto-refresh if needed
 * Call this on app startup/login to ensure notifications work
 * 
 * @param token - JWT auth token for API calls
 * @returns Promise<{valid: boolean, resubscribed: boolean, message: string}>
 */
export async function validateAndRefreshSubscription(token) {
  if (!isPushSupported()) {
    return { valid: false, resubscribed: false, message: 'Push not supported' };
  }

  if (!token) {
    return { valid: false, resubscribed: false, message: 'No auth token' };
  }

  // Check if we have notification permission
  if (Notification.permission !== 'granted') {
    return { valid: false, resubscribed: false, message: 'Notifications not permitted' };
  }

  try {
    // Get current local subscription (uses getRegistration — never hangs)
    const registration = await navigator.serviceWorker.getRegistration('/');
    if (!registration) {
      return { valid: false, resubscribed: false, message: 'No service worker' };
    }
    const localSubscription = await registration.pushManager.getSubscription();

    if (!localSubscription) {
      // No local subscription - user hasn't subscribed
      return { valid: false, resubscribed: false, message: 'Not subscribed' };
    }

    // Check if this subscription exists on the server
    const checkResponse = await fetch(`${API_URL}/api/push/check-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ endpoint: localSubscription.endpoint })
    });

    if (checkResponse.ok) {
      const checkData = await checkResponse.json();
      
      if (checkData.exists && checkData.valid) {
        console.log('[Push] Subscription is valid');
        return { valid: true, resubscribed: false, message: 'Subscription valid' };
      }
      
      // Subscription exists but may be invalid - test it
      if (checkData.exists && !checkData.valid) {
        console.log('[Push] Subscription invalid, re-subscribing...');
        // Unsubscribe locally and re-subscribe
        await localSubscription.unsubscribe();
        await subscribeToPush(token);
        return { valid: true, resubscribed: true, message: 'Re-subscribed successfully' };
      }
    }

    // Subscription not found on server - re-subscribe
    console.log('[Push] Subscription not found on server, registering...');
    await subscribeToPush(token);
    return { valid: true, resubscribed: true, message: 'Subscription registered' };

  } catch (err) {
    console.error('[Push] Validation error:', err);
    return { valid: false, resubscribed: false, message: err.message };
  }
}

/**
 * Force refresh the push subscription
 * Use this when subscriptions are known to be invalid
 * 
 * @param token - JWT auth token for API calls
 */
export async function forceRefreshSubscription(token) {
  if (!isPushSupported()) {
    throw new Error('Push notifications not supported');
  }
  if (!token) {
    throw new Error('Authentication required');
  }

  console.log('[Push] Force refreshing subscription...');

  // Tear down existing local subscription
  const registration = await navigator.serviceWorker.getRegistration('/');
  if (registration) {
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      await existing.unsubscribe();
      // Best-effort server cleanup (don't block on it)
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 5000);
      try {
        await fetch(`${API_URL}/api/push/unsubscribe`, {
          method: 'DELETE',
          signal: ctrl.signal,
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ endpoint: existing.endpoint })
        });
      } catch (_) {}
      clearTimeout(t);
    }
  }

  // subscribeToPush will create a fresh subscription with full timeout protection
  return subscribeToPush(token);
}

